import { useState } from 'react';

interface ServicesProps {
  onOpenModal: () => void;
}

export default function Services({ onOpenModal }: ServicesProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  const services = [
    {
      icon: 'ri-palette-line',
      title: 'Fine Art & Gallery Pieces',
      description: 'Museum-quality framing for paintings, prints, and gallery artwork using archival materials and expert color matching.',
    },
    {
      icon: 'ri-heart-line',
      title: 'Personal Treasures & Collections',
      description: 'Preserve your cherished memories, family heirlooms, photographs, and memorabilia with custom framing designed to last generations.',
    },
    {
      icon: 'ri-building-line',
      title: 'Studios & Designers',
      description: 'Professional framing services for interior designers, production studios, and corporate clients with volume pricing and quick turnaround.',
    },
    {
      icon: 'ri-camera-line',
      title: 'Photography Framing',
      description: 'Expert framing for professional photographers and personal photo collections with archival mounting and UV-protective glazing.',
    },
    {
      icon: 'ri-trophy-line',
      title: 'Sports Memorabilia',
      description: 'Shadow box and display case framing for jerseys, signed items, and collectibles with museum-grade preservation techniques.',
    },
    {
      icon: 'ri-ancient-pavilion-line',
      title: 'Antique Restoration',
      description: 'Careful restoration and reframing of antique pieces, vintage artwork, and heirloom items using period-appropriate materials.',
    },
  ];

  const additionalServices = [
    { icon: 'ri-shirt-line', name: 'Jersey & Textile Framing' },
    { icon: 'ri-medal-line', name: 'Awards & Certificates' },
    { icon: 'ri-scissors-cut-line', name: 'Custom Matting' },
    { icon: 'ri-image-line', name: 'Canvas Stretching' },
    { icon: 'ri-gift-line', name: 'Plexiglass' },
    { icon: 'ri-tools-line', name: 'Frame Repair' },
  ];

  const visibleServices = [
    services[currentIndex % services.length],
    services[(currentIndex + 1) % services.length],
    services[(currentIndex + 2) % services.length],
  ];

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + services.length) % services.length);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % services.length);
  };

  return (
    <section id="services" className="py-12 md:py-24 bg-[#F5F3EE]">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="mb-8 md:mb-16">
          <div className="inline-block mb-2 md:mb-4">
            <i className="ri-diamond-line text-[#D4B137] text-2xl md:text-3xl"></i>
          </div>
          <h3 className="text-2xl md:text-5xl font-serif font-bold text-[#2E4A3E] mb-3 md:mb-4">
            Comprehensive Framing Solutions for Every Need
          </h3>
          <p className="text-gray-600 text-sm md:text-lg max-w-2xl mb-4 md:mb-6">
            From fine art to family treasures, we bring expertise and artistry to every project
          </p>
        </div>

        <div className="relative">
          <div className="grid md:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
            {visibleServices.map((service, index) => (
              <div
                key={`${currentIndex}-${index}`}
                className="bg-white rounded-xl md:rounded-2xl p-5 md:p-8 shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer"
              >
                <div className="w-10 h-10 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-[#D4B137]/10 mb-4 md:mb-6">
                  <i className={`${service.icon} text-2xl md:text-3xl text-[#D4B137]`}></i>
                </div>
                <h4 className="text-lg md:text-2xl font-serif font-bold text-black mb-2 md:mb-4">
                  {service.title}
                </h4>
                <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-center gap-3 md:gap-4">
            <button
              onClick={handlePrev}
              className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white shadow-md hover:shadow-lg hover:bg-[#D4B137]/10 transition-all cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-xl md:text-2xl text-[#2E4A3E]"></i>
            </button>
            <div className="flex items-center gap-2">
              {services.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
                    index === currentIndex % services.length
                      ? 'bg-[#D4B137] w-6'
                      : 'bg-[#2E4A3E]/20 hover:bg-[#2E4A3E]/40'
                  }`}
                />
              ))}
            </div>
            <button
              onClick={handleNext}
              className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white shadow-md hover:shadow-lg hover:bg-[#D4B137]/10 transition-all cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-xl md:text-2xl text-[#2E4A3E]"></i>
            </button>
          </div>
        </div>

        <div className="hidden md:block mt-16 bg-white rounded-2xl p-8 shadow-sm">
          <h4 className="text-xl md:text-2xl font-serif font-bold text-[#2E4A3E] mb-6 text-center">
            Additional Specialties
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {additionalServices.map((service, index) => (
              <div
                key={index}
                className="flex flex-col items-center gap-3 p-4 rounded-xl hover:bg-[#F5F3EE] transition-colors cursor-pointer"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#D4B137]/10">
                  <i className={`${service.icon} text-2xl text-[#D4B137]`}></i>
                </div>
                <span className="text-black text-sm text-center font-medium">{service.name}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 md:mt-12 text-center">
          <button
            onClick={onOpenModal}
            className="inline-flex items-center gap-2 bg-[#D4B137] text-black px-5 md:px-6 py-2.5 md:py-3 rounded-lg font-medium hover:bg-[#C4A127] transition-colors cursor-pointer whitespace-nowrap text-sm md:text-base"
          >
            <i className="ri-calendar-line text-base md:text-lg"></i>
            Book a Consultation
          </button>
        </div>
      </div>
    </section>
  );
}
